import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, CheckBox, FlexBox, Icon, Input, List, ListItemStandard, MessageStrip, SegmentedButton, SegmentedButtonItem, Select, Option, Tag, Text, Title, BusyIndicator } from '@ui5/webcomponents-react';
import { api, type AccessKind, type AccessLevel, type ScreenAccessData } from '../../api/client';
import { PageHeader } from '../../components/Responsive';
import { useIsMobile } from '../../hooks/useIsMobile';

type Subject = { kind: AccessKind; code: string; name: string; hint?: string; superUser: boolean; role?: string };
type Grants = Record<string, AccessLevel>;
const KINDS: { key: AccessKind; label: string }[] = [{ key: 'user', label: 'App users' }, { key: 'role', label: 'Roles' }, { key: 'sapuser', label: 'SAP users' }];
const count = (g?: Grants) => (g ? Object.keys(g).length : 0);
const grantsOf = (d: ScreenAccessData, kind: AccessKind, code: string) => d.grants.find(r => r.kind === kind && r.code.toLowerCase() === code.toLowerCase());

/** What a subject sees today, in words — shown in the subject list and above the editor. */
function describe(d: ScreenAccessData, s: Subject): { text: string; state: 'custom' | 'inherited' | 'all' | 'standard' } {
  if (s.superUser) return { text: s.kind === 'sapuser' ? 'SAP superuser — all screens' : 'Full access — all screens', state: 'all' };
  const own = grantsOf(d, s.kind, s.code);
  if (own) return { text: `Only ${count(own.grants)} selected screen(s)`, state: 'custom' };
  if (s.kind === 'user' && s.role) {
    const role = grantsOf(d, 'role', s.role);
    if (role) return { text: `From role ${s.role} — ${count(role.grants)} screen(s)`, state: 'inherited' };
    if (d.roles.find(r => r.code.toLowerCase() === s.role!.toLowerCase())?.superUser) return { text: `Role ${s.role} has full access — all screens`, state: 'all' };
  }
  return { text: s.kind === 'sapuser' ? 'All screens (SAP authorisations apply)' : 'Standard rules (role permissions)', state: 'standard' };
}

/**
 * Screen Access: the system administrator decides exactly which screens each app user, role or SAP user sees,
 * and whether each is read-only or editable. A user's own list wins over their role's; without either the
 * standard rules apply (Menu & Access roles + the role's entity permissions). Stored per company.
 */
export function ScreenAccessAdmin() {
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const data = useQuery({ queryKey: ['screen-access'], queryFn: api.screenAccess });
  const [kind, setKind] = useState<AccessKind>((params.get('kind') as AccessKind) || 'user');
  const [code, setCode] = useState<string | null>(params.get('code'));
  const [q, setQ] = useState('');

  const d = data.data;
  const subjects = useMemo<Subject[]>(() => {
    if (!d) return [];
    if (kind === 'user') return d.users.map(u => ({ kind, code: u.code, name: u.name, role: u.role, hint: [u.role && `Role ${u.role}`, !u.active && 'inactive'].filter(Boolean).join(' · '), superUser: !!u.role && !!d.roles.find(r => r.code.toLowerCase() === u.role!.toLowerCase())?.superUser }));
    if (kind === 'role') return d.roles.map(r => ({ kind, code: r.code, name: r.name, superUser: r.superUser }));
    return d.sapUsers.map(u => ({ kind, code: u.code, name: u.name, superUser: u.superUser }));
  }, [d, kind]);
  const shown = subjects.filter(s => !q || `${s.code} ${s.name}`.toLowerCase().includes(q.toLowerCase()));
  const selected = subjects.find(s => s.code === code);

  const pick = (k: AccessKind, c: string | null) => { setKind(k); setCode(c); setParams(c ? { kind: k, code: c } : {}, { replace: true }); };

  if (data.isLoading) return <FlexBox justifyContent="Center" style={{ padding: '3rem' }}><BusyIndicator active /></FlexBox>;
  if (data.error || !d) return <MessageStrip design="Negative" hideCloseButton>{(data.error as Error)?.message ?? 'Could not load screen access'}</MessageStrip>;

  const subjectPane = (
    <div style={{ width: isMobile ? '100%' : '20rem', flexShrink: 0 }}>
      <SegmentedButton style={{ width: '100%', marginBottom: '0.5rem' }} onSelectionChange={e => pick((e.detail.selectedItems[0] as HTMLElement).dataset.k as AccessKind, null)}>
        {KINDS.filter(k => k.key !== 'sapuser' || d.sapChannels.length).map(k => <SegmentedButtonItem key={k.key} data-k={k.key} selected={kind === k.key}>{k.label}</SegmentedButtonItem>)}
      </SegmentedButton>
      <Input placeholder="Search" icon={<Icon name="search" />} value={q} style={{ width: '100%', marginBottom: '0.5rem' }} onInput={e => setQ(e.target.value ?? '')} />
      {kind === 'sapuser' && d.sapUsersError && <MessageStrip design="Critical" hideCloseButton>{d.sapUsersError}</MessageStrip>}
      <List onItemClick={e => pick(kind, (e.detail.item as HTMLElement).dataset.code ?? null)} style={{ maxHeight: isMobile ? undefined : 'calc(100vh - 16rem)', overflow: 'auto' }}>
        {shown.map(s => {
          const st = describe(d, s);
          return <ListItemStandard key={s.code} data-code={s.code} selected={s.code === code} text={`${s.name}${s.name !== s.code ? ` (${s.code})` : ''}`}
            description={[s.hint, st.text].filter(Boolean).join(' — ')} icon={st.state === 'custom' ? 'locked' : st.state === 'inherited' ? 'chain-link' : st.state === 'all' ? 'unlocked' : 'employee'} />;
        })}
      </List>
    </div>
  );

  return (
    <div>
      <PageHeader title={`Screen Access — ${d.companyDb}`} />
      <MessageStrip design="Information" hideCloseButton style={{ marginBottom: '0.75rem' }}>
        Choose exactly which screens a user can see, and whether each is read-only or editable. A user’s own selection
        overrides their role’s; with neither, the standard rules apply (Menu &amp; Access roles and the role’s permissions).
        Granting a screen also grants the data it shows. Changes apply on the user’s next page load — no sign-out needed.
      </MessageStrip>
      <FlexBox style={{ gap: '1rem', alignItems: 'flex-start' }}>
        {(!isMobile || !selected) && subjectPane}
        {selected
          ? <AccessEditor key={`${selected.kind}:${selected.code}`} d={d} subject={selected} onBack={isMobile ? () => pick(kind, null) : undefined} />
          : !isMobile && <FlexBox direction="Column" alignItems="Center" style={{ flex: 1, padding: '3rem 1rem', color: 'var(--sapNeutralTextColor)' }}><Icon name="locked" style={{ width: '2.5rem', height: '2.5rem', marginBottom: '0.5rem' }} /><Text>Select a user, role or SAP user to manage the screens they can see.</Text></FlexBox>}
      </FlexBox>
    </div>
  );
}

function AccessEditor({ d, subject, onBack }: { d: ScreenAccessData; subject: Subject; onBack?: () => void }) {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const own = grantsOf(d, subject.kind, subject.code);
  const inherited = subject.kind === 'user' && subject.role ? grantsOf(d, 'role', subject.role) : undefined;
  const [custom, setCustom] = useState(!!own);
  const [grants, setGrants] = useState<Grants>(own?.grants ?? {});
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ design: 'Positive' | 'Negative'; text: string } | null>(null);
  // Re-sync only when the saved row actually changes (not on every background refetch, which would drop edits).
  useEffect(() => { setCustom(!!own); setGrants(own?.grants ?? {}); }, [own?.updatedAt, !!own]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = describe(d, subject);
  const dirty = custom !== !!own || (custom && JSON.stringify(grants) !== JSON.stringify(own?.grants ?? {}));
  const f = filter.toLowerCase();
  const visibleScreens = d.screens.filter(s => !f || `${s.title} ${s.entity} ${s.group} ${s.subgroup ?? ''}`.toLowerCase().includes(f));
  const groupMap = new Map<string, typeof d.screens>();
  for (const s of visibleScreens) (groupMap.get(s.group) ?? groupMap.set(s.group, []).get(s.group)!).push(s);
  const groups = [...groupMap.entries()];
  const editCount = Object.values(grants).filter(l => l === 'edit').length;

  const set = (ids: string[], level: AccessLevel | null) => setGrants(g => { const n = { ...g }; for (const id of ids) { if (level) n[id] = level; else delete n[id]; } return n; });
  const toggleOpen = (g: string) => setOpen(o => { const n = new Set(o); n.has(g) ? n.delete(g) : n.add(g); return n; });
  const startCustom = () => { setCustom(true); if (!own) setGrants(inherited?.grants ?? {}); };
  const copyFrom = (key: string) => { const [k, ...c] = key.split(':'); const row = grantsOf(d, k as AccessKind, c.join(':')); if (row) setGrants({ ...row.grants }); };

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      if (custom) await api.setScreenAccess(subject.kind, subject.code, grants);
      else if (own) await api.clearScreenAccess(subject.kind, subject.code);
      await qc.invalidateQueries({ queryKey: ['screen-access'] });
      qc.invalidateQueries({ queryKey: ['screens'] });
      setMsg({ design: 'Positive', text: custom ? `Saved — ${subject.name} now sees ${count(grants)} screen(s).` : `Saved — ${subject.name} uses the standard rules.` });
    } catch (e) { setMsg({ design: 'Negative', text: (e as Error).message }); } finally { setBusy(false); }
  };

  const others = d.grants.filter(r => !(r.kind === subject.kind && r.code === subject.code));

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <FlexBox wrap="Wrap" alignItems="Center" justifyContent="SpaceBetween" style={{ gap: '0.5rem', marginBottom: '0.5rem' }}>
        <FlexBox alignItems="Center" style={{ gap: '0.5rem', minWidth: 0 }}>
          {onBack && <Button icon="nav-back" design="Transparent" onClick={onBack} />}
          <div style={{ minWidth: 0 }}>
            <Title level="H4" style={{ margin: 0 }}>{subject.name}</Title>
            <Text style={{ color: 'var(--sapNeutralTextColor)' }}>{KINDS.find(k => k.key === subject.kind)?.label.replace(/s$/, '')} · {subject.code} · now: {status.text}</Text>
          </div>
        </FlexBox>
        {!subject.superUser && <FlexBox wrap="Wrap" alignItems="Center" style={{ gap: '0.5rem' }}>
          <Button disabled={!dirty || busy} onClick={() => { setCustom(!!own); setGrants(own?.grants ?? {}); setMsg(null); }}>Discard</Button>
          <Button design="Emphasized" icon="save" disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</Button>
        </FlexBox>}
      </FlexBox>
      {msg && <MessageStrip design={msg.design} onClose={() => setMsg(null)} style={{ marginBottom: '0.5rem' }}>{msg.text}</MessageStrip>}

      {subject.superUser ? (
        <MessageStrip design="Information" hideCloseButton>Administrators with full access always see every screen. To limit this user, give them a role without full access first.</MessageStrip>
      ) : (<>
        <SegmentedButton style={{ marginBottom: '0.5rem' }} onSelectionChange={e => ((e.detail.selectedItems[0] as HTMLElement).dataset.m === 'custom' ? startCustom() : setCustom(false))}>
          <SegmentedButtonItem data-m="standard" selected={!custom}>{subject.kind === 'user' && inherited ? `Same as role ${subject.role}` : subject.kind === 'sapuser' ? 'All screens' : 'Standard rules'}</SegmentedButtonItem>
          <SegmentedButtonItem data-m="custom" selected={custom}>Only selected screens</SegmentedButtonItem>
        </SegmentedButton>

        {!custom ? (
          <MessageStrip design="Information" hideCloseButton>
            {subject.kind === 'user' && inherited ? `${subject.name} sees the ${count(inherited.grants)} screen(s) selected for role ${subject.role}.`
              : subject.kind === 'user' ? `${subject.name} sees the screens their role’s permissions allow${subject.role ? ` (role ${subject.role})` : ''}.`
              : subject.kind === 'role' ? 'Members see the screens this role’s permissions allow (Roles tab), filtered by the Menu & Access roles column.'
              : 'This SAP user sees every screen; SAP’s own authorisations still decide what they can read or change.'}
            {' '}Choose “Only selected screens” to pick them one by one.
          </MessageStrip>
        ) : (<>
          <FlexBox wrap="Wrap" alignItems="Center" style={{ gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Input placeholder="Filter screens" icon={<Icon name="search" />} value={filter} style={{ width: '14rem', maxWidth: '100%' }} onInput={e => setFilter(e.target.value ?? '')} />
            <Button onClick={() => set(visibleScreens.map(s => s.id), 'view')}>{filter ? 'Grant shown' : 'Grant all'} (view)</Button>
            <Button onClick={() => set(visibleScreens.map(s => s.id), 'edit')}>{filter ? 'Grant shown' : 'Grant all'} (edit)</Button>
            <Button design="Transparent" onClick={() => set(visibleScreens.map(s => s.id), null)}>{filter ? 'Remove shown' : 'Remove all'}</Button>
            <Button design="Transparent" icon={open.size ? 'collapse-group' : 'expand-group'} onClick={() => setOpen(open.size ? new Set() : new Set(groups.map(([g]) => g)))}>{open.size ? 'Collapse all' : 'Expand all'}</Button>
            {others.length > 0 && <Select style={{ width: '13rem' }} onChange={e => { const k = e.detail.selectedOption.dataset.k; if (k) copyFrom(k); }}>
              <Option data-k="" selected>Copy from…</Option>
              {others.map(r => <Option key={`${r.kind}:${r.code}`} data-k={`${r.kind}:${r.code}`}>{`${KINDS.find(k => k.key === r.kind)?.label.replace(/s$/, '')} ${r.code} (${count(r.grants)})`}</Option>)}
            </Select>}
          </FlexBox>
          <FlexBox wrap="Wrap" alignItems="Center" style={{ gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Tag colorScheme="6" hideStateIcon>{count(grants)} of {d.screens.length} screens</Tag>
            <Tag colorScheme="8" hideStateIcon>{editCount} editable</Tag>
            {count(grants) === 0 && <Text style={{ color: 'var(--sapCriticalTextColor)' }}>This {subject.kind === 'role' ? 'role' : 'user'} will see no screens.</Text>}
          </FlexBox>

          <div style={{ border: '1px solid var(--sapList_BorderColor, #e5e5e5)', borderRadius: '0.5rem', background: 'var(--sapList_Background, #fff)' }}>
            {groups.map(([g, list]) => {
              const ids = list.map(s => s.id);
              const on = ids.filter(id => grants[id]).length;
              const allEdit = on > 0 && ids.every(id => !grants[id] || grants[id] === 'edit');
              const expanded = open.has(g) || !!filter;
              return (
                <div key={g} style={{ borderBottom: '1px solid var(--sapList_BorderColor, #e5e5e5)' }}>
                  <FlexBox alignItems="Center" style={{ gap: '0.25rem', padding: '0.15rem 0.5rem', background: 'var(--sapList_GroupHeaderBackground, #f7f7f7)' }}>
                    <Button design="Transparent" icon={expanded ? 'navigation-down-arrow' : 'navigation-right-arrow'} onClick={() => toggleOpen(g)} />
                    <CheckBox checked={on === ids.length} indeterminate={on > 0 && on < ids.length} onChange={e => set(ids, e.target.checked ? 'view' : null)} />
                    <Text style={{ fontWeight: 600, flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => toggleOpen(g)}>{g}</Text>
                    <Text style={{ color: 'var(--sapNeutralTextColor)', whiteSpace: 'nowrap' }}>{on} / {ids.length}</Text>
                    <CheckBox text={isMobile ? 'Edit' : 'All editable'} disabled={on === 0} checked={allEdit} onChange={e => set(ids.filter(id => grants[id]), e.target.checked ? 'edit' : 'view')} />
                  </FlexBox>
                  {expanded && list.map(s => (
                    <FlexBox key={s.id} alignItems="Center" style={{ gap: '0.25rem', padding: '0 0.5rem 0 2.75rem', borderTop: '1px solid var(--sapList_BorderColor, #f0f0f0)' }}>
                      <CheckBox checked={!!grants[s.id]} onChange={e => set([s.id], e.target.checked ? 'view' : null)} />
                      <FlexBox direction="Column" style={{ flex: 1, minWidth: 0, padding: '0.3rem 0' }}>
                        <Text>{s.subgroup ? `${s.subgroup} · ` : ''}{s.title}</Text>
                        <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)' }}>{s.entity}{s.channels.length ? ` · ${s.channels.join('/')} only` : ''}</Text>
                      </FlexBox>
                      {s.packOff && <Tag colorScheme="1" hideStateIcon>pack off</Tag>}
                      <CheckBox text="Edit" disabled={!grants[s.id]} checked={grants[s.id] === 'edit'} onChange={e => set([s.id], e.target.checked ? 'edit' : 'view')} />
                    </FlexBox>
                  ))}
                </div>
              );
            })}
            {groups.length === 0 && <Text style={{ display: 'block', padding: '1rem' }}>No screens match “{filter}”.</Text>}
          </div>
        </>)}
      </>)}
    </div>
  );
}
